import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart3, TrendingUp, TrendingDown, Eye, MousePointer, 
  Heart, MessageCircle, Share2, Search, Users, MapPin,
  Clock, Activity, Zap, Globe, Calendar, RefreshCw,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';

export default function AdminStatistics() {
  const { adminAPI } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Données
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [spotsStats, setSpotsStats] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [searches, setSearches] = useState(null);
  const [tags, setTags] = useState(null);

  useEffect(() => {
    loadAllStats();
  }, [period]);

  // Auto-refresh pour les stats temps réel
  useEffect(() => {
    if (activeTab === 'realtime') {
      const interval = setInterval(() => {
        loadRealtime();
      }, 30000); // Refresh toutes les 30 secondes
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const loadAllStats = async () => {
    setLoading(true);
    try {
      const days = parseInt(period);
      
      // Charger toutes les stats en parallèle
      const [overviewData, timelineData, spotsData, engagementData, realtimeData, searchesData, tagsData] = 
        await Promise.all([
          adminAPI.getStatsOverview(days),
          adminAPI.getStatsTimeline(days, 'day'),
          adminAPI.getStatsSpots(20, 'views'),
          adminAPI.getStatsEngagement(days),
          adminAPI.getStatsRealtime(),
          adminAPI.getStatsSearches(days),
          adminAPI.getStatsTags()
        ]);
      
      setOverview(overviewData);
      setTimeline(timelineData);
      setSpotsStats(spotsData);
      setEngagement(engagementData);
      setRealtime(realtimeData);
      setSearches(searchesData);
      setTags(tagsData);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      const data = await adminAPI.getStatsRealtime();
      setRealtime(data);
    } catch (error) {
      console.error('Failed to load realtime stats:', error);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return { icon: Minus, color: 'text-gray-500', text: 'N/A' };
    const change = ((current - previous) / previous) * 100;
    if (change > 0) return { icon: ArrowUp, color: 'text-green-600', text: `+${change.toFixed(1)}%` };
    if (change < 0) return { icon: ArrowDown, color: 'text-red-600', text: `${change.toFixed(1)}%` };
    return { icon: Minus, color: 'text-gray-500', text: '0%' };
  };

  // Composant de mini-graphique en barres
  const MiniBarChart = ({ data, dataKey = 'count', maxBars = 14, color = 'bg-blue-500' }) => {
    if (!data || data.length === 0) return <div className="text-gray-400 text-sm">Aucune donnée</div>;
    
    const displayData = data.slice(-maxBars);
    const maxValue = Math.max(...displayData.map(d => d[dataKey] || 0), 1);
    
    return (
      <div className="flex items-end gap-1 h-16">
        {displayData.map((item, index) => (
          <div
            key={index}
            className={`${color} rounded-t flex-1 min-w-[4px] transition-all hover:opacity-80`}
            style={{ height: `${((item[dataKey] || 0) / maxValue) * 100}%`, minHeight: '2px' }}
            title={`${item.period || item.date || item.hour}: ${item[dataKey] || 0}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Chargement des statistiques...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Statistiques & Analytics</h1>
          <p className="text-gray-600 mt-1">Analysez les performances de votre plateforme</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
              <SelectItem value="365">Cette année</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadAllStats}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Tabs de navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="spots">Spots</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="searches">Recherches</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="realtime">Temps réel</TabsTrigger>
        </TabsList>

        {/* ===== ONGLET VUE D'ENSEMBLE ===== */}
        <TabsContent value="overview" className="space-y-6">
          {/* Statistiques principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">Vues</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.views)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {overview?.rates?.avg_views_per_day}/jour en moyenne
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <MousePointer className="h-4 w-4" />
                  <span className="text-sm">Clics</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.clicks)}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Heart className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Likes</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.likes)}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <MessageCircle className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Commentaires</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.comments)}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Share2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Partages</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.shares)}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">Sessions</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(overview?.totals?.unique_sessions)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Taux d'engagement global */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Évolution des vues
                </CardTitle>
                <CardDescription>
                  Nombre de vues par jour sur la période sélectionnée
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MiniBarChart 
                  data={timeline?.data} 
                  dataKey="views" 
                  maxBars={30}
                  color="bg-blue-500"
                />
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{timeline?.data?.[0]?.period}</span>
                  <span>{timeline?.data?.[timeline?.data?.length - 1]?.period}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Taux d'engagement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-600">
                    {overview?.rates?.engagement_rate}%
                  </p>
                  <p className="text-sm text-gray-500">Engagement global</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Interactions totales</span>
                    <span className="font-medium">{formatNumber(overview?.totals?.total_interactions)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Moy. interactions/jour</span>
                    <span className="font-medium">{overview?.rates?.avg_interactions_per_day}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Graphique des interactions */}
          <Card>
            <CardHeader>
              <CardTitle>Évolution des interactions</CardTitle>
              <CardDescription>Likes, commentaires et partages sur la période</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium">Likes</span>
                  </div>
                  <MiniBarChart 
                    data={timeline?.data} 
                    dataKey="likes" 
                    color="bg-red-400"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Commentaires</span>
                  </div>
                  <MiniBarChart 
                    data={timeline?.data} 
                    dataKey="comments" 
                    color="bg-blue-400"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Share2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Partages</span>
                  </div>
                  <MiniBarChart 
                    data={timeline?.data} 
                    dataKey="shares" 
                    color="bg-green-400"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ONGLET SPOTS ===== */}
        <TabsContent value="spots" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Top des spots par vues
              </CardTitle>
              <CardDescription>
                Les spots les plus consultés sur la plateforme
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {spotsStats?.spots?.map((spot, index) => (
                  <div 
                    key={spot.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold
                        ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                          index === 1 ? 'bg-gray-200 text-gray-700' : 
                          index === 2 ? 'bg-orange-100 text-orange-700' : 
                          'bg-gray-100 text-gray-600'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{spot.title}</h4>
                        <p className="text-sm text-gray-500">Par {spot.author}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Eye className="h-4 w-4" />
                          <span className="font-medium">{formatNumber(spot.views)}</span>
                        </div>
                        <span className="text-xs text-gray-400">vues</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-red-600">
                          <Heart className="h-4 w-4" />
                          <span className="font-medium">{spot.likes}</span>
                        </div>
                        <span className="text-xs text-gray-400">likes</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-blue-600">
                          <MessageCircle className="h-4 w-4" />
                          <span className="font-medium">{spot.comments}</span>
                        </div>
                        <span className="text-xs text-gray-400">comm.</span>
                      </div>
                      <div className="text-center">
                        <Badge variant={spot.engagement_score > 5 ? "default" : "secondary"}>
                          {spot.engagement_score}%
                        </Badge>
                        <span className="text-xs text-gray-400 block">engage.</span>
                      </div>
                    </div>
                  </div>
                ))}
                {(!spotsStats?.spots || spotsStats.spots.length === 0) && (
                  <p className="text-center text-gray-500 py-8">Aucune donnée disponible</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ONGLET ENGAGEMENT ===== */}
        <TabsContent value="engagement" className="space-y-6">
          {/* Taux détaillés */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <MousePointer className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold text-purple-600">
                  {engagement?.rates?.click_through_rate}%
                </p>
                <p className="text-sm text-gray-500">Taux de clic</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Heart className="h-6 w-6 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-red-600">
                  {engagement?.rates?.like_rate}%
                </p>
                <p className="text-sm text-gray-500">Taux de like</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <MessageCircle className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-blue-600">
                  {engagement?.rates?.comment_rate}%
                </p>
                <p className="text-sm text-gray-500">Taux de commentaire</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Share2 className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold text-green-600">
                  {engagement?.rates?.share_rate}%
                </p>
                <p className="text-sm text-gray-500">Taux de partage</p>
              </CardContent>
            </Card>
          </div>

          {/* Évolution hebdomadaire */}
          <Card>
            <CardHeader>
              <CardTitle>Évolution de l'engagement par semaine</CardTitle>
            </CardHeader>
            <CardContent>
              <MiniBarChart 
                data={engagement?.weekly_engagement} 
                dataKey="interactions" 
                maxBars={12}
                color="bg-purple-500"
              />
              {engagement?.weekly_engagement && (
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{engagement.weekly_engagement[0]?.week}</span>
                  <span>{engagement.weekly_engagement[engagement.weekly_engagement.length - 1]?.week}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Résumé des totaux */}
          <Card>
            <CardHeader>
              <CardTitle>Résumé des interactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{formatNumber(engagement?.totals?.views)}</p>
                  <p className="text-sm text-gray-500">Vues totales</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{formatNumber(engagement?.totals?.clicks)}</p>
                  <p className="text-sm text-gray-500">Clics totaux</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{formatNumber(engagement?.totals?.likes)}</p>
                  <p className="text-sm text-gray-500">Likes totaux</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{formatNumber(engagement?.totals?.comments)}</p>
                  <p className="text-sm text-gray-500">Commentaires</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{formatNumber(engagement?.totals?.shares)}</p>
                  <p className="text-sm text-gray-500">Partages</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ONGLET RECHERCHES ===== */}
        <TabsContent value="searches" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Top recherches
                </CardTitle>
                <CardDescription>
                  Les termes les plus recherchés par les utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {searches?.top_searches?.map((search, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                        <span className="font-medium">{search.query || '(vide)'}</span>
                      </div>
                      <Badge variant="secondary">{search.count} recherches</Badge>
                    </div>
                  ))}
                  {(!searches?.top_searches || searches.top_searches.length === 0) && (
                    <p className="text-center text-gray-500 py-4">Aucune recherche enregistrée</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Volume de recherches</CardTitle>
                <CardDescription>Nombre de recherches par jour</CardDescription>
              </CardHeader>
              <CardContent>
                <MiniBarChart 
                  data={searches?.daily_searches} 
                  dataKey="count" 
                  maxBars={30}
                  color="bg-orange-500"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== ONGLET TAGS ===== */}
        <TabsContent value="tags" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Popularité des tags
              </CardTitle>
              <CardDescription>
                Tags les plus utilisés et les plus recherchés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {tags?.tags?.map((tag, index) => (
                  <div 
                    key={index}
                    className="p-3 border border-gray-200 rounded-lg hover:border-green-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-sm">#{tag.name}</Badge>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{tag.spots_count} spots</span>
                      <span>{tag.searches} rech.</span>
                    </div>
                  </div>
                ))}
                {(!tags?.tags || tags.tags.length === 0) && (
                  <p className="col-span-full text-center text-gray-500 py-8">Aucun tag disponible</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ONGLET TEMPS RÉEL ===== */}
        <TabsContent value="realtime" className="space-y-6">
          {/* Indicateur de mise à jour */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Mise à jour automatique toutes les 30s</span>
            </div>
            <Button size="sm" variant="outline" onClick={loadRealtime}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Stats temps réel */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Zap className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <p className="text-3xl font-bold text-green-700">{realtime?.active_sessions || 0}</p>
                <p className="text-sm text-green-600">Sessions actives</p>
                <p className="text-xs text-gray-500 mt-1">Dernière heure</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{realtime?.events?.last_15min || 0}</p>
                <p className="text-sm text-gray-500">15 dernières min</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Activity className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{realtime?.events?.last_hour || 0}</p>
                <p className="text-sm text-gray-500">Dernière heure</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                <p className="text-2xl font-bold">{formatNumber(realtime?.events?.last_24h)}</p>
                <p className="text-sm text-gray-500">24 dernières heures</p>
              </CardContent>
            </Card>
          </div>

          {/* Activité horaire */}
          <Card>
            <CardHeader>
              <CardTitle>Activité horaire (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              <MiniBarChart 
                data={realtime?.hourly_activity} 
                dataKey="count" 
                maxBars={24}
                color="bg-indigo-500"
              />
            </CardContent>
          </Card>

          {/* Derniers événements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Derniers événements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {realtime?.recent_events?.map((event, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                  >
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="outline" 
                        className={
                          event.event_type === 'view' ? 'border-blue-300 text-blue-700' :
                          event.event_type === 'like' ? 'border-red-300 text-red-700' :
                          event.event_type === 'comment' ? 'border-green-300 text-green-700' :
                          'border-gray-300 text-gray-700'
                        }
                      >
                        {event.event_type}
                      </Badge>
                      <span className="text-gray-600">
                        {event.target_type} {event.target_id ? `(${event.target_id.slice(0, 8)}...)` : ''}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {event.timestamp ? new Date(event.timestamp).toLocaleTimeString('fr-FR') : ''}
                    </span>
                  </div>
                ))}
                {(!realtime?.recent_events || realtime.recent_events.length === 0) && (
                  <p className="text-center text-gray-500 py-4">Aucun événement récent</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
